"use client";

// StoreSync — the single client-side sync driver.
//
// Mounted once in the root layout. It keeps the stores FRESH; it is no longer
// what makes them CORRECT. The first dump now arrives server-side, in the HTML,
// via <StoreSeed> — this poll is what notices everyone else's writes afterwards,
// every 10 s while the tab is shown (below), through the merge + pending-write
// guard that keeps it from stomping the acting user's own in-flight change.
//
// ─── AND IT SAYS SO WHEN IT STOPS WORKING ───────────────────────────────────
//
// A failing poll used to be a console.warn and a `catch {}` labelled "keep
// last-known state", which is a polite way of describing a screen that has
// quietly stopped being true. Nobody has an open console. The page looks
// perfect. Meanwhile an AM is reading a week as it stood twenty minutes ago,
// approving against it, and wondering later why the creator says they had
// already re-submitted.
//
// This is the READ half of what lib/write-failure.ts does for writes, and it is
// deliberately quieter: a stale read is a smaller harm than a lost write — the
// truth is still on the server and arrives the moment the poll recovers — so it
// gets a bar rather than a modal, and only after the failures have gone on long
// enough to mean something.
//
// ─── AND IT IS QUIET WHEN NOBODY IS LOOKING ─────────────────────────────────
//
// The poll used to run every 10 s in every tab, hidden ones included, plus once
// on `focus` AND once on `visibilitychange`, so one tab switch was two full
// dumps. Now, with the same hidden-tab rule as the Affiliates auto-refresh
// (useRefreshWhile):
//
//   - no interval while the tab is hidden: nothing is scheduled at all;
//   - one poll when it is shown again, then the 10 s interval starts afresh;
//   - focus and visibility are one wake, not two: a wake while a poll is in
//     flight, or within WAKE_QUIET_MS of the last one starting, adds nothing;
//   - never two polls at once, from any source (tick, wake, Try again);
//   - a failure while hidden is not counted: a sleeping laptop's dropped
//     request is not an outage, and the bar is for someone who is looking.
//
// The mount poll still runs once, hidden or not. It is cheap: the document's
// seed sets the cursor (components/store-seed.tsx), so it asks only for what
// was written since the server read the seed, and is a 304 when nothing was. It
// is kept for that catch-up, and because a tab whose shell arrived in an RSC
// answer (the sign-in landing) has no seed and no cursor: its mount poll is the
// whole store.
//
// ─── AND IT ASKS ONLY FOR WHAT CHANGED ──────────────────────────────────────
//
// A tab starts with the seed's cursor (epoch, seq), or with none and takes the
// whole store on its first poll; every poll sends the cursor back and gets a
// 304 when nothing moved, or only the rows written and the keys deleted since.
// Deletions are new: until the diff, a row deleted on the server stayed in
// every other open tab until a reload. A cursor the server no longer
// recognises (it restarted) is answered with the whole store again, which
// replaces what the tab holds. The pending-write guard still decides, per row,
// whether the server's copy may overwrite this tab's, and it trusts a poll
// asked after the tab's write landed (pollMark).
//
// ─── AND IT ASKS ONLY ABOUT THE GROUPS THE TAB READS ────────────────────────
//
// A tab holds the groups its pages read
// (lib/route-store-groups.ts): the document's seed brings the first page's,
// and every poll names them (`groups=`), so the diff carries nothing a page in
// this tab has not asked for. A client navigation into a page that reads a
// group the tab does not hold wants it (lib/store-cursor.ts), and a poll goes
// out at once with that group as `load=`: the server sends it whole BESIDE the
// diff, in the same read, and the one cursor moves for every group together.
// Loaded separately, a write to the new group between the two reads would be
// in neither answer, or a write to a held one would arrive twice. Until it
// lands, the page shows the group gate's loading state
// (components/store-group-gate.tsx).
//
// A navigation that has LANDED loads a group, and so does someone showing they
// mean to follow a link (lib/store-intent.ts):
// pointing at it, focusing it or touching it asks for its page's groups, so
// the load overlaps the time between hover and click. Next's own prefetch
// renders no page in this tree, and so asks for nothing.
//
// ─── AND IT NOTICES A DEPLOY ────────────────────────────────────────────────
//
// Every answer, the 304 included, carries the server's build id in a header.
// When it is not the build this page was drawn by, the tab is running an old
// client, and its next navigation to another page is a full page load
// (lib/stale-tab.ts). Nothing reloads in place. The poll's own scheduling is
// unchanged.

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { applyAllStores, type StoreKey, type StoreRow } from "@/lib/store-hydrate";
import {
  heldGroups, noteGroupLoad, resyncStoreNext, setStoreCursor, setStorePoll, storeCursor, takeResync, wantGroups, wantedGroups,
} from "@/lib/store-cursor";
import { formatGroups, unionGroups, type StoreGroup } from "@/lib/store-groups";
import { storeGroupsFor } from "@/lib/route-store-groups";
import { reportClientError } from "@/lib/client-error-report";
import { applyingPoll, pollMark } from "@/lib/store-persist";
import { BUILD_META } from "@/lib/stale-build";
import { staleTab, watchStaleLinks } from "@/lib/stale-tab";

/**
 * How many polls in a row must fail before we interrupt.
 *
 * ONE FAILURE IS WEATHER. A laptop waking up, a deploy swapping a container, a
 * tunnel reconnecting — all of those drop a single request and fix themselves
 * before the next tick. A bar that flashed on every one of them would be trained
 * away inside a day, and then it would not work on the morning it mattered.
 *
 * THREE IS ABOUT THIRTY SECONDS, which is long enough that something is actually
 * wrong and short enough that nobody has yet made a decision on stale data.
 */
const STALE_AFTER = 3;

/** The poll's cadence while the tab is shown. */
export const POLL_EVERY_MS = 10_000;

/**
 * A wake (focus, or the tab shown) this soon after a poll began adds no second
 * one. Showing a tab fires `visibilitychange` and then `focus`, a few ms apart;
 * a dump on localhost can land in between, so the in-flight guard alone would
 * still let the pair through as two.
 */
export const WAKE_QUIET_MS = 2_000;

/** GET /api/store's answer (app/api/store/route.ts). Every field is optional to the reader: an old server sends `{ rows }` alone. */
interface PollAnswer {
  epoch?: string;
  seq?: number;
  full?: boolean;
  rows?: StoreRow[];
  deleted?: StoreKey[];
  /** The groups whose WHOLE dump `rows` carries. */
  groups?: StoreGroup[];
}

/**
 * The poll's address: the tab's cursor, unless it has none or asked to start
 * again; every group it holds or wants; and, beside a cursor, the wanted ones
 * as `load`. Without a cursor every named group comes whole anyway.
 */
function pollUrl(at: { epoch: string; seq: number } | null, groups: StoreGroup[], load: StoreGroup[]): string {
  const q = new URLSearchParams();
  if (at) {
    q.set("since", String(at.seq));
    q.set("epoch", at.epoch);
  }
  q.set("groups", formatGroups(groups));
  if (at && load.length > 0) q.set("load", formatGroups(load));
  return `/api/store?${q.toString()}`;
}

export function StoreSync() {
  const [stale, setStale] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Refs, not state: the poll runs on an interval that closes over its own
  // scope, and a counter in state would give every tick a stale copy. Nothing
  // renders off these directly — `stale` is the rendered fact.
  const failures = useRef(0);
  const reported = useRef(false);

  // ONE useCallback, WITH THE FAILURE PATH INSIDE IT. Splitting the counting out
  // into a sibling function would make it a dependency of this one — recreated
  // every render, and so either a stale closure or a memo that never holds. It
  // only touches refs and a setter, both of which are stable, so keeping it here
  // costs nothing and leaves the dependency array honestly empty.
  const refresh = useCallback(async () => {
    const onFailure = (status: number | undefined) => {
      // Not while hidden: nobody is reading this page, and a laptop asleep in a
      // background tab is weather, not an outage. The next poll is on being shown.
      if (document.hidden) return;
      failures.current += 1;
      if (failures.current < STALE_AFTER) return;
      setStale(true);
      // ONCE PER OUTAGE, NOT ONCE PER TICK. A server down for an hour is 360
      // polls, and 360 identical log lines is not 360 times the information.
      // Reset by the first success, so a second outage reports again.
      if (reported.current) return;
      reported.current = true;
      reportClientError({
        error: new Error(`store sync has failed ${failures.current} times in a row`),
        source: "write-refused",
        context: { surface: "store-sync", consecutive: failures.current, ...(status === undefined ? {} : { status }) },
      });
    };

    const succeeded = () => {
      failures.current = 0;
      reported.current = false;
      setStale(false);
    };

    // The cursor this tab is at, unless it has asked to start again (a refused
    // DELETE; store-cursor.ts). With none, the server sends every group named
    // whole. The groups: what the tab holds, and what a page has asked for.
    const resync = takeResync();
    const at = resync ? null : storeCursor();
    const load = wantedGroups();
    const groups = unionGroups(heldGroups(), load);
    const url = pollUrl(at, groups, load);
    // Every write answered by now is in this answer, or something newer is
    // (lib/store-persist.ts, pollMark).
    const mark = pollMark();
    // A page may be waiting on the load: the gate says loading while it is out,
    // and that it did not load when it fails (lib/store-cursor.ts).
    const loading = load.length > 0;
    if (loading) noteGroupLoad("sent");
    try {
      const res = await fetch(url, { cache: "no-store" });
      // Which build the server runs, on every answer (f3). A different one from
      // this page's makes the next navigation a document load (lib/stale-tab.ts).
      staleTab().noteServerBuild(res.headers?.get(BUILD_META) ?? null);
      // NOTHING CHANGED: no body, nothing to apply. It says nothing about a
      // group the tab does not hold (the server never answers 304 to a load),
      // so it marks none held: a page waiting on one keeps waiting.
      if (res.status === 304) {
        succeeded();
        return;
      }
      if (!res.ok) {
        // A permanently-failing poll used to be invisible: the app rendered fine
        // and simply never synced. Say so once per failure rather than swallowing
        // it — and, past the threshold, say so on the screen too.
        console.warn("[store-sync] /api/store responded", res.status);
        if (resync) resyncStoreNext(); // still owed
        if (loading) noteGroupLoad("failed");
        onFailure(res.status);
        return;
      }
      const answer = (await res.json()) as PollAnswer;
      // A FULL DUMP SENT IN PLACE OF THE DIFF WE ASKED FOR is a resync: our
      // cursor was counted on another file (a restart, a restore, a reseed), so
      // the server cannot say what was deleted since — the dump replaces what
      // the tab holds of the groups it carries, and anything of theirs it no
      // longer carries is gone. A full dump we asked for (no cursor) only adds:
      // it is the first, or a heal. A group LOADED beside a diff only adds too:
      // the tab held nothing of it. Each dump makes its groups held, and a dump
      // of `scripts` has the grades held asked for again
      // (lib/store-hydrate.ts). A dump names its groups: every server this
      // tab can meet, the release before included, sends them.
      const full = answer.full === true;
      const dumped = answer.groups ?? [];
      applyingPoll(mark, () => applyAllStores(answer.rows ?? [], answer.deleted ?? [], full && at !== null ? dumped : false, dumped));
      if (typeof answer.epoch === "string" && typeof answer.seq === "number") {
        setStoreCursor({ epoch: answer.epoch, seq: answer.seq });
      }
      succeeded();
    } catch {
      if (resync) resyncStoreNext();
      if (loading) noteGroupLoad("failed");
      onFailure(undefined); // keep last-known state, and start counting
    }
  }, []);

  // ONE POLL AT A TIME, whoever asks: the interval, a wake, or Try again. A dump
  // that takes longer than a tick (a slow link, a busy server) used to have the
  // next one stacked behind it. A second ask joins the one in flight.
  const inFlight = useRef<Promise<void> | null>(null);
  const lastStart = useRef(0);
  const poll = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    lastStart.current = Date.now();
    const p = refresh().finally(() => {
      inFlight.current = null;
    });
    inFlight.current = p;
    return p;
  }, [refresh]);

  // A poll asked for from outside (pollStoreNow, lib/store-cursor.ts). It waits
  // out one already in flight rather than join it: that one was asked before
  // whatever the caller is waiting for, so its answer may not carry it.
  useEffect(() => setStorePoll(async () => {
    if (inFlight.current) await inFlight.current;
    await poll();
  }), [poll]);

  // A stale tab's in-app link clicks become document loads (lib/stale-tab.ts).
  // Listening from mount costs one check per click, and does nothing until a
  // poll has said the server moved on.
  useEffect(() => watchStaleLinks(), []);

  // THE PAGE ON SCREEN READS GROUPS THE TAB MAY NOT HOLD: ask for them now,
  // not on the next tick, because the page is showing the group gate's loading
  // state until they land. Before the mount poll's effect, so a tab with no
  // seed (the sign-in landing) asks for its page's groups in that first poll.
  //
  // A poll already in flight was asked before this page wanted anything, so it
  // is waited out and the load goes in the next one. A load that fails is
  // owed still, and the interval asks again; the gate keeps waiting, and three
  // failures put up the bar below as for any poll.
  const pathname = usePathname();
  useEffect(() => {
    if (!wantGroups(storeGroupsFor(pathname ?? "/"))) return;
    void (async () => {
      await poll();
      if (wantedGroups().length > 0) await poll();
    })();
  }, [pathname, poll]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = () => {
      clearInterval(timer);
      timer = undefined;
    };
    // The interval runs only while the tab is shown, and restarts from now.
    const start = () => {
      stop();
      if (!document.hidden) timer = setInterval(poll, POLL_EVERY_MS);
    };
    // Focus and the tab being shown: one handler, so the pair is one poll.
    // Hidden: stop, and schedule nothing until it is shown again.
    const wake = () => {
      if (document.hidden) return stop();
      if (!inFlight.current && Date.now() - lastStart.current >= WAKE_QUIET_MS) {
        void poll();
        start(); // the next tick is a full interval after this poll, not sooner
      } else if (timer === undefined) start(); // shown again with a poll already under way
    };
    void poll(); // on mount, hidden or not: a 304 after a seeded document load
    start();
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    return () => {
      stop();
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [poll]);

  if (!stale) return null;

  // BOTTOM RIGHT, opposite the write-failure bar. The two can be up at once —
  // the same outage that refuses a write refuses the poll behind it — and two
  // red bars stacked on the same corner would read as one confused thing rather
  // than as two facts about the same problem.
  return (
    <div
      data-testid="sync-stale-bar"
      role="status"
      className="fixed bottom-4 right-4 z-[55] flex items-center gap-3 rounded-lg border border-amber-500/40 bg-surface px-3 py-2 shadow-lg"
    >
      <AlertTriangle size={14} className="shrink-0 text-amber-400" aria-hidden />
      <span className="text-[11.5px] text-fg">
        Not syncing — this page may be out of date.
      </span>
      <button
        onClick={async () => {
          setRetrying(true);
          try {
            await poll();
          } finally {
            setRetrying(false);
          }
        }}
        disabled={retrying}
        data-testid="sync-stale-retry"
        className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-text-muted transition-colors hover:border-line-strong hover:text-fg disabled:opacity-50"
      >
        <RefreshCw size={11} className={retrying ? "animate-spin" : undefined} />
        {retrying ? "Trying…" : "Try again"}
      </button>
    </div>
  );
}
